import { useState } from 'react';
import { MIN_CART_ITEM_QUANTITY, MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL } from '../../constants/cart.constants';
import { AddToCartErrorTypeEnum } from '../../enums/add-to-cart-error-type.enum';
import type { CartItemInterface, ConfigurableCartItem, ConfigurableVariant, Maybe } from '../../types/front.graphql';
import { CartItemTypeEnum } from '../../models/cart.model';
import { getCartItemVariation as getCartItemVariationUtil } from '../../helpers/cart-item.helpers';
import isEmpty from 'lodash/isEmpty';
import { useCheckoutCart } from './checkout-cart.hook';
import { type ProductModel } from '@kidslabs/module-catalog';
import { isNumber } from '@kidslabs/module-core';

type CartActionProps = {
    item: CartItemInterface;
    value: number | string;
};

type AvailableAddToCartActionProps = CartActionProps & { isGift: boolean };

interface CheckoutCartActionsHook {
    handleIncreaseQuantity: (props: CartActionProps) => void;
    handleDecreaseQuantity: (props: CartActionProps) => void;
    handleChangeQuantity: (props: CartActionProps) => void;
    validateAddToCartAction: (props: CartActionProps) => AddToCartErrorTypeEnum | undefined;
    getCartItemVariation: (props: CartActionProps) => ConfigurableVariant | undefined;
    getAvailableAddToCartAction: (props: AvailableAddToCartActionProps) => boolean;
    handleRemoveItemInCart: (props: CartActionProps) => void;
    handleRemoveItemsInCart: (props: CartActionProps[]) => void;
    loading: boolean;
    errorMessage?: string;
    initCart: (cartId: string) => void;
    isItemInCart: (item: CartItemInterface) => boolean;
    getItemInCart: (item: CartItemInterface) => Maybe<CartItemInterface> | undefined;
}

export const useCheckoutCartActions = (): CheckoutCartActionsHook => {
    const {
        addItem,
        removeItem,
        updateItem,
        getItem,
        getCartId,
        initCart: initCheckoutCart,
        isItemInCart: isItemInCheckoutCart,
    } = useCheckoutCart();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const getCartItemVariation = (props: CartActionProps): ConfigurableVariant | undefined => {
        const { item } = props;

        const typeName = (item as { __typename?: string }).__typename;
        const isConfigurable =
            typeName === CartItemTypeEnum.CONFIGURABLE || typeName === CartItemTypeEnum.CONFIGURABLE_LOCAL;

        // Use type assertion since __typename may not be explicitly typed in CartItemInterface
        if (isConfigurable) {
            return getCartItemVariationUtil(item as ConfigurableCartItem);
        }
        return undefined;
    };

    const validateAddToCartAction = (props: CartActionProps): AddToCartErrorTypeEnum | undefined => {
        const { item } = props;
        if (loading) {
            return AddToCartErrorTypeEnum.CART_IS_INITIALIZING;
        }

        const variation = getCartItemVariation(props);

        if (
            '__typename' in (item?.product ?? {}) &&
            (item?.product as ProductModel)?.__typename === 'ConfigurableProduct' &&
            !variation
        ) {
            return AddToCartErrorTypeEnum.MISSING_SELECTED_VARIANT;
        }

        return undefined;
    };

    const getAvailableAddToCartAction = ({ item, isGift }: AvailableAddToCartActionProps) => {
        return item.id.startsWith('new-') || isGift;
    };

    const handleIncreaseQuantity = (props: CartActionProps) => {
        setLoading(true);
        setErrorMessage(undefined);
        const { item, value } = props;
        const quantity = isNumber(value) ? Number(value) : 1;
        const cartItem = getItem(item.id);
        if (cartItem) {
            updateItem(item.id, { quantity: (cartItem.quantity || 0) + quantity });
        } else {
            addItem({ ...item, quantity });
        }
        setLoading(false);
    };

    const handleDecreaseQuantity = (props: CartActionProps) => {
        setLoading(true);
        setErrorMessage(undefined);
        const { item } = props;
        const cartId = getCartId();
        const cartItem = getItem(item.id);

        if (isEmpty(cartId) || cartItem?.id.startsWith('new-')) {
            setLoading(false);
            return;
        }
        if (!cartItem || (cartItem.quantity || 0) <= 1) {
            setLoading(false);
            return;
        }
        updateItem(item.id, { quantity: (cartItem.quantity || 0) - 1 });
        setLoading(false);
    };

    const handleChangeQuantity = (props: CartActionProps) => {
        setLoading(true);
        setErrorMessage(undefined);
        const { item, value } = props;
        let updateQuantity = isNumber(value) ? Number(value) : MIN_CART_ITEM_QUANTITY;
        if (updateQuantity < MIN_CART_ITEM_QUANTITY) {
            setLoading(false);
            return;
        }
        if (updateQuantity > MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL) {
            updateQuantity = MAX_CART_ITEM_QUANTITY_PRODUCT_DETAIL;
        }
        updateItem(item.id, { quantity: updateQuantity });
        setLoading(false);
    };

    const handleRemoveItemInCart = (props: CartActionProps) => {
        setLoading(true);
        setErrorMessage(undefined);
        const { item } = props;
        removeItem(item.id);
        setLoading(false);
    };

    const handleRemoveItemsInCart = (cartActionProps: CartActionProps[]) => {
        setLoading(true);
        setErrorMessage(undefined);
        cartActionProps.forEach(({ item }) => removeItem(item.id));
        setLoading(false);
    };

    const initCart = (cartId: string) => {
        if (isEmpty(cartId)) {
            setErrorMessage('Cart ID is required to initialize the cart.');
            return;
        }
        initCheckoutCart(cartId);
    };

    const isItemInCart = (item: CartItemInterface) => {
        return isItemInCheckoutCart(item);
    };

    const getItemInCart = (item: CartItemInterface) => {
        if (!item || !item.id) return undefined;
        return getItem(item.id);
    };

    return {
        handleIncreaseQuantity,
        handleDecreaseQuantity,
        handleChangeQuantity,
        validateAddToCartAction,
        getCartItemVariation,
        getAvailableAddToCartAction,
        handleRemoveItemInCart,
        handleRemoveItemsInCart,
        initCart,
        loading,
        errorMessage,
        isItemInCart,
        getItemInCart: getItemInCart,
    };
};
